/* eslint-disable camelcase */
import {
  useState,
  useEffect,
  SyntheticEvent,
  ReactElement,
  ChangeEvent
} from 'react';
import Checkbox from '@mui/material/Checkbox';
import {TreeView} from '@mui/x-tree-view';
import '@mui/lab';
import {TreeItem, TreeItemProps} from '@mui/x-tree-view/TreeItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';

import {TreeItemWrapper} from './CustomTreeViewStyle';

import {getFolderContents} from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import {
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import {useSelectedDocumentContext} from '@context/SelectedDocumentContext';
import AuthUtils from 'src/utils/AuthUtils';
import {useModuleContext} from '@context/ModuleContext';
import FileThumbnail from 'src/minimal-components/file-thumbnail/file-thumbnail';
import {useAppContext} from '@context/AppContext';

type StyledTreeItemProps = TreeItemProps & {
  labelText: string;
  nodeExtensions: string[] | [];
  bgColor?: string;
  color?: string;
  nodeId?: string;
  type?: File | string;
  hideDocumentViewer?: boolean | undefined;
};

let docIdArrays: any = [];

const StyledTreeItem = (props: StyledTreeItemProps) => {
  const theme = useTheme();
  const {
    labelText,
    nodeExtensions,
    nodeId = 'defaultNodeId',
    hideDocumentViewer,
    type,
    ...other
  } = props;

  const docId = AuthUtils.getSelectedDocID();
  const isSelected = props?.nodeId?.toString() === docId;

  const {
    setSelectedDocumentData,
    removeSelectedDocumentData,
    SelectedDocumentState
  } = useSelectedDocumentContext();

  const onCheckboxChange = (
    e: ChangeEvent<HTMLInputElement>,
    nodeID: string,
    type: any
  ) => {
    // if type === 0 it is a folder

    if (type !== 0) {
      if (SelectedDocumentState.selectionData.length === 0) {
        setSelectedDocumentData({data: nodeID});
      } else {
        docIdArrays = [...SelectedDocumentState.selectionData];
        const index = docIdArrays.indexOf(nodeID);
        if (index > -1) {
          removeSelectedDocumentData({data: index});
        } else {
          setSelectedDocumentData({data: nodeID});
        }
      }
    }
  };

  return (
    <TreeItemWrapper isfiletype={`${!!nodeExtensions.length}`}>
      {hideDocumentViewer !== undefined ? (
        <div
          style={{
            width: 'min-content',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'center'
          }}>
          <FormControlLabel
            label={''}
            key={nodeId}
            sx={{margin: 0}}
            control={
              <Checkbox
                key={nodeId}
                onChange={event =>
                  onCheckboxChange(event, nodeId, nodeExtensions.length)
                }
              />
            }
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '6px',
              paddingLeft: '0'
            }}>
            <TreeItem
              nodeId={nodeId}
              label={
                <Stack direction="row" alignItems="center" spacing={1} my={0.5}>
                  <FileThumbnail
                    tooltip={true}
                    file={type || ''}
                    sx={{width: 15, height: 15}}
                  />
                  <Tooltip title={labelText} placement="top-start">
                    <Typography
                      noWrap
                      variant="inherit"
                      sx={{
                        // maxWidth: 360,
                        cursor: 'pointer',
                        textOverflow: 'clip',
                        overflow: 'unset',
                        paddingRight: '1vh',
                        color: theme.palette.common.black,
                        ...(!!nodeExtensions.length && {
                          fontWeight: 'bold'
                        })
                      }}>
                      {labelText}
                    </Typography>
                  </Tooltip>
                </Stack>
              }
              {...other}
            />
          </div>
        </div>
      ) : (
        <TreeItem
          nodeId={nodeId}
          label={
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              my={type === 'openfolder' ? 0 : 0.5}>
              <FileThumbnail
                tooltip={true}
                file={type || ''}
                sx={{width: 15, height: 15}}
              />
              <Tooltip title={labelText} placement="top-start">
                <Typography
                  noWrap
                  variant="inherit"
                  sx={{
                    // maxWidth: 360,
                    cursor: 'pointer',
                    textOverflow: 'clip',
                    overflow: 'unset',
                    paddingRight: '1vh',
                    color: theme.palette.common.black,
                    ...((type === 'openfolder' || isSelected) && {
                      fontWeight: 'bold',
                      ...(isSelected && {
                        color: theme.palette.primary.main
                      })
                    })
                  }}>
                  {labelText}
                </Typography>
              </Tooltip>
            </Stack>
          }
          {...other}
        />
      )}
    </TreeItemWrapper>
  );
};

interface DocumentFolder {
  ID: number;
  ParentDocumentTypeID: null | number;
  DocumentTypeDomainID: number;
  Code: string;
  Name: string;
  Description: string;
  isActive: string;
  Sequence: string;
  ChildNodes: any[];
  HasDocuments: boolean;
  FileExtensions: [];
  SourceDocumentID?: string;
  IsNotes?: boolean[];
  files_count?:number;
  Doc_Document_ID?:any
}

interface Document {
  ID: number;
  LegacyApp: string;
  LegacyMRN: string;
  SourceDocumentID: string;
  FileExtensions: string[];
  IsNotes: boolean[];
  Name: string;
  IsMultiPage: string;
  Sequence: number[];
  DocumentTypeID: number;
  ChildNodes?: any[];
  HasDocuments?: boolean;
  files_count?:number;
  Doc_Document_ID?:any
}

interface CustomTreeViewProps {
  node: any;
  setDocData?: any;
  folderContentsQuery: string;
  module: string;
  subModule: string;
  userId: string;
  sqlVariables: any;
  docDownloadData?: string[];
  cardValues?: any;
  legacyApp?: any;
  unique_id?: string;
  menuItem?: string;
  hideDocumentViewer?: boolean | undefined;
  sectionUIData: any;
  date_range?: any;
  setIframeSrc?: any;
  recordId?: string;
  procedure_type?: string;
  accession_number?: string;
  specimen_number_id?: string;
  searchTerm?: string;
}

const CustomTreeView = ({
  node,
  setDocData,
  folderContentsQuery,
  module,
  subModule,
  userId,
  sqlVariables,
  docDownloadData,
  cardValues,
  legacyApp,
  unique_id,
  menuItem,
  hideDocumentViewer,
  sectionUIData,
  date_range,
  setIframeSrc,
  recordId,
  procedure_type,
  accession_number,
  specimen_number_id,
  searchTerm
}: CustomTreeViewProps): ReactElement => {
  const theme = useTheme();
  const [childNodes, setChildNodes] = useState<(Document | DocumentFolder)[]>(
    []
  );
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [isNodeToggled, setIsNodeToggle] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isSearchMode = !!searchTerm?.trim();

  const {
    appState: {applicationName}
  } = useAppContext();
  const {moduleState} = useModuleContext();
  const {isBH, isBHReason}: any = moduleState;

  const getLocalStorageData = AuthUtils.getSelectedCheckBoxData();

  const fetchChildNodes = async (parentNode: DocumentFolder | Document) => {
    if (setIframeSrc) {
      setIframeSrc('');
    }
    let folderContents: Document[] = [];
    const {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      ChildNodes: childNodes,
      HasDocuments: hasDocuments,
      ID: id,files_count:files_count,Doc_Document_ID:Doc_Document_ID
    } = parentNode;
    if (childNodes) {
      folderContents = [...childNodes];
    }
    if (hasDocuments) {
      const sql = {
        folderContentsQuery: `${folderContentsQuery}`
      };

      const data: any = {
        sql: sql,
        variables: sqlVariables,
        date_range: date_range ?? {},
        filter_schema_clause: sectionUIData.filter_schema_clause,
        isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
        isBH_reason: isBHReason,
        application_name: applicationName,
        legacyMRN: recordId,
        procedure_type,
        accession_number,specimen_number_id,
        isSearchMode:isSearchMode,
        Doc_Document_ID,files_count

      };

      if (
        sectionUIData?.is_filter_sql_required &&
        getLocalStorageData.length > 0
      ) {
        data.filter_data = getLocalStorageData;
      }

      const encryptRequestData = EncryptUtils.encryptedData(data);
      const response = await getFolderContents(
        module,
        subModule,
        id,
        userId,
        unique_id || '',
        {tk: encryptRequestData},
        legacyApp,
        menuItem || ''
      );

      folderContents = [...folderContents, ...response.documents];
    }
    return folderContents;
  };

  const handleFolderChange = async (event: SyntheticEvent, nodes: string[]) => {
    setIsNodeToggle(!isNodeToggled);
    const expandingNodes: string[] = nodes.filter(
      eachNode => !expandedNodes.includes(eachNode)
    );
    setExpandedNodes(expandingNodes);
    if (setIframeSrc) {
      setIframeSrc('');
    }
    if (expandingNodes[0]) {
      setIsLoading(true);
      const response = await fetchChildNodes(node);

      setChildNodes(response);
      setIsLoading(false);
    }
  };

  const getTreeItem = (): ReactElement[] | ReactElement => {
    if (childNodes.length) {
      return childNodes.filter(Boolean).map((eachNode: Document | DocumentFolder) => (
        <CustomTreeView
          hideDocumentViewer={hideDocumentViewer}
          key={eachNode.ID}
          node={eachNode}
          setDocData={setDocData}
          folderContentsQuery={folderContentsQuery}
          module={module}
          subModule={subModule}
          userId={userId}
          sqlVariables={sqlVariables}
          docDownloadData={docDownloadData || []}
          cardValues={cardValues}
          legacyApp={legacyApp}
          unique_id={unique_id}
          menuItem={menuItem || ''}
          sectionUIData={sectionUIData}
          date_range={date_range ?? {}}
          recordId={recordId}
          procedure_type={procedure_type}
          accession_number={accession_number}
          specimen_number_id={specimen_number_id}
          searchTerm={searchTerm}
        />
      ));
    } else {
      return <div id="empty-div" />;
    }
  };

  const renderTreeItem = (
    currentNode: Document | DocumentFolder
  ): ReactElement => {
    const {FileExtensions: fileExtensions = [], ID: id, Name: name} = currentNode || ({} as any);
    const fileType =
      fileExtensions.length > 0 ? fileExtensions[0]?.toLowerCase() : '';
    const isFolderOpen = !fileExtensions?.length
      ? isSearchMode
        ? childNodes.length > 0
        : isNodeToggled
      : false;
    const type = !fileExtensions?.length
      ? isFolderOpen
        ? 'openfolder'
        : 'folder'
      : fileExtensions?.length > 1
        ? 'multi'
        : fileType;

    if (!fileExtensions?.length) {
      return (
        <StyledTreeItem
          hideDocumentViewer={hideDocumentViewer}
          nodeId={id.toString()}
          key={name}
          labelText={name}
          type={type}
          nodeExtensions={fileExtensions || []}>
          {isLoading ? <ComponentLoader /> : getTreeItem()}
        </StyledTreeItem>
      );
    } else {
      return (
        <StyledTreeItem
          hideDocumentViewer={hideDocumentViewer}
          nodeId={id.toString()}
          key={id}
          labelText={name}
          type={type}
          nodeExtensions={fileExtensions}
          onClick={() => {
            if (setIframeSrc) {
              setIframeSrc('');
            }
            setDocData(node);
          }}
        />
      );
    }
  };

  useEffect(() => {
    if (isSearchMode) {
      if (Array.isArray((node as any)?.children) && (node as any).children.length > 0) {
        setChildNodes((node as any).children);
      } else if (Array.isArray(node?.ChildNodes)) {
        setChildNodes(node.ChildNodes);
      } else {
        setChildNodes([]);
      }
    } else if (!isSearchMode) {
      setChildNodes([]);
    }
  }, [isSearchMode, node]);

  return (
    <TreeView
      expanded={isSearchMode && childNodes.length > 0 ? [node.ID.toString()] : undefined}
      defaultCollapseIcon={
        <ArrowDropDownIcon style={{color: theme.palette.text.disabled}} />
      }
      defaultExpandIcon={
        <ArrowRightIcon style={{color: theme.palette.text.disabled}} />
      }
      defaultEndIcon={''}
      onNodeToggle={handleFolderChange}>
      {renderTreeItem(node)}
    </TreeView>
  );
};

export default CustomTreeView;
